import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Download,
  ExternalLink,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError, openApiDocument } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { formatDate, formatINR } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { GatedSection } from '../components/shared/GatedSection';
import { FeeStatusBadge } from '../components/finance/StatusBadge';
import {
  cycleLabel,
  type FeeCycle,
  type FeePayment,
  type FeePaymentStudentRef,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/receipts/$paymentId')({
  component: ReceiptDetailPage,
});

// ---------------------------------------------------------------------------
// Detail response shape — backend extends GET /api/fees/payments/:id to embed
// household + contacts and confirmedBy. Until that ships, every embed is
// optional so this page renders against today's `{payment, student, cycle}`
// shape as well as the future enriched response.
// ---------------------------------------------------------------------------

interface HouseholdContact {
  id: string;
  name: string;
  relation: string | null;
  phone: string | null;
  email: string | null;
}

interface HouseholdRef {
  id: string;
  primaryContactName: string;
  contacts?: HouseholdContact[];
}

interface UserRef {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface ClaimSummary {
  id: string;
  status: 'pending' | 'confirmed' | 'rejected';
  createdAt: string;
  claimedBy: string;
  claimedByName?: string | null;
}

interface ReceiptDetail {
  payment: FeePayment;
  student: FeePaymentStudentRef;
  cycle: FeeCycle;
  household?: HouseholdRef | null;
  confirmedBy?: UserRef | null;
  claim?: ClaimSummary | null;
}

const ADMIN_ROLES = ['super_admin', 'finance_admin'] as const;

const DAY_MS = 1000 * 60 * 60 * 24;

const daysSinceDate = (yyyyMmDd: string): number => {
  const t = Date.parse(`${yyyyMmDd}T00:00:00Z`);
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / DAY_MS);
};

function ReceiptDetailPage(): JSX.Element {
  const { paymentId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role !== null && (ADMIN_ROLES as readonly string[]).includes(role);

  const [overflowOpen, setOverflowOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['fee-receipt', 'detail', paymentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ReceiptDetail>(
        `/api/fees/payments/${encodeURIComponent(paymentId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const data = detailQuery.data;
  const payment = data?.payment;
  const student = data?.student;
  const cycle = data?.cycle;

  useEffect(() => {
    if (!overflowOpen) return;
    const handler = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest('[data-receipt-overflow]')) setOverflowOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  const openReceipt = async (download: boolean): Promise<void> => {
    try {
      await openApiDocument(`/api/fees/payments/${encodeURIComponent(paymentId)}/receipt-url`, { download });
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open receipt.', { variant: 'error' });
    }
  };

  const handleDownload = (): Promise<void> => openReceipt(true);
  const handleOpenNewTab = (): Promise<void> => openReceipt(false);

  // The receipt-regenerate endpoint is shipping in the parallel backend slice
  // (§4.14). The button POSTs and surfaces success/failure; the cache is
  // invalidated so the UI picks up a fresh receipt number when one is issued.
  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/fees/payments/${encodeURIComponent(paymentId)}/receipt/regenerate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Receipt PDF regeneration started.', { variant: 'success' });
      void queryClient.invalidateQueries({
        queryKey: ['fee-receipt', 'detail', paymentId],
      });
      setOverflowOpen(false);
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not regenerate.', {
        variant: 'error',
      });
    },
  });

  const handleCopyReceiptNumber = async (): Promise<void> => {
    if (!payment?.receiptNumber) return;
    try {
      await navigator.clipboard.writeText(payment.receiptNumber);
      toast('Receipt number copied.', { variant: 'success' });
    } catch {
      toast('Could not copy receipt number.', { variant: 'error' });
    }
    setOverflowOpen(false);
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!payment) return [];
    const receiptStatus = payment.receiptNumber ? 'Generated' : 'Pending';
    return [
      { label: 'Amount paid', value: formatINR(payment.netAmountPaise) },
      {
        label: 'Discount',
        value:
          payment.discountPaise > 0 ? formatINR(payment.discountPaise) : '—',
      },
      {
        label: 'Days since due',
        value: daysSinceDate(payment.dueDate),
        sub: payment.paidAt ? 'paid' : 'unpaid',
      },
      { label: 'Receipt status', value: receiptStatus },
    ];
  }, [payment]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Receipt"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !data || !payment || !student || !cycle ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`Receipt ${payment.receiptNumber ?? 'pending'}`}
              sublabel={
                <>
                  <span className="font-medium text-[color:var(--color-text)]">
                    {student.name}
                  </span>
                  {student.admissionNumber ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{student.admissionNumber}</span>
                    </>
                  ) : null}
                </>
              }
              statusChip={
                <div className="flex flex-wrap items-center gap-1.5">
                  <FeeStatusBadge status={payment.status} />
                  <CycleChip cycle={cycle} />
                  <ProviderChip provider={payment.provider} />
                </div>
              }
              backTo="/finance/receipts"
              actions={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download size={14} aria-hidden="true" />}
                    onClick={() => {
                      void handleDownload();
                    }}
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ExternalLink size={14} aria-hidden="true" />}
                    onClick={() => {
                      void handleOpenNewTab();
                    }}
                  >
                    Open in new tab
                  </Button>
                  <ReceiptOverflowMenu
                    open={overflowOpen}
                    onOpenChange={setOverflowOpen}
                    paymentId={payment.id}
                    receiptNumber={payment.receiptNumber}
                    canRegenerate={isAdmin}
                    regenerating={regenerateMutation.isPending}
                    onRegenerate={() => regenerateMutation.mutate()}
                    onCopy={() => {
                      void handleCopyReceiptNumber();
                    }}
                  />
                </>
              }
            />

            <KpiStrip items={kpiItems} />

            <div className="grid gap-4 lg:grid-cols-2">
              <PaymentDetailsCard
                payment={payment}
                confirmedBy={data.confirmedBy ?? null}
              />
              <StudentHouseholdCard
                student={student}
                household={data.household ?? null}
              />
              <FeeCycleCard cycle={cycle} />
              <ClaimHistoryCard claim={data.claim ?? null} />
              <ReceiptPdfCard payment={payment} />
            </div>

            <ActivityPanel
              targetType="fee_payment"
              targetId={payment.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header chips
// ---------------------------------------------------------------------------

function CycleChip({ cycle }: { cycle: FeeCycle }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--color-text)]">
      {cycleLabel(cycle.year, cycle.month)}
    </span>
  );
}

function ProviderChip({
  provider,
}: {
  provider: FeePayment['provider'];
}): JSX.Element {
  const label = provider === 'razorpay' ? 'Razorpay' : 'Manual';
  return (
    <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-medium capitalize text-[color:var(--color-text-muted)]">
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Overflow menu
// ---------------------------------------------------------------------------

interface ReceiptOverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  paymentId: string;
  receiptNumber: string | null;
  canRegenerate: boolean;
  regenerating: boolean;
  onRegenerate: () => void;
  onCopy: () => void;
}

function ReceiptOverflowMenu({
  open,
  onOpenChange,
  paymentId,
  receiptNumber,
  canRegenerate,
  regenerating,
  onRegenerate,
  onCopy,
}: ReceiptOverflowMenuProps): JSX.Element {
  return (
    <div className="relative" data-receipt-overflow>
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        loading={regenerating}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-9 z-10 min-w-[220px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          {canRegenerate ? (
            <button
              role="menuitem"
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none disabled:opacity-50"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Regenerate PDF
            </button>
          ) : null}
          <button
            role="menuitem"
            type="button"
            onClick={onCopy}
            disabled={!receiptNumber}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none disabled:opacity-50"
          >
            <Copy size={14} aria-hidden="true" />
            Copy receipt number
          </button>
          <a
            role="menuitem"
            href={`/finance/fees/payments/${encodeURIComponent(paymentId)}`}
            onClick={() => onOpenChange(false)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] no-underline hover:bg-[color:var(--color-surface)] focus:outline-none"
          >
            <ExternalLink size={14} aria-hidden="true" />
            View payment record
          </a>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  title: string;
  children: ReactNode;
  className?: string;
}

const Card = ({ title, children, className }: CardProps): JSX.Element => (
  <section
    className={clsx(
      'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5',
      className,
    )}
  >
    <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {title}
    </h3>
    <div className="mt-3 space-y-2 text-sm text-[color:var(--color-text)]">
      {children}
    </div>
  </section>
);

const Field = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): JSX.Element => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
    <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {label}
    </dt>
    <dd className="min-w-0 text-right text-sm text-[color:var(--color-text)]">
      {value ?? <span className="text-[color:var(--color-text-muted)]">—</span>}
    </dd>
  </div>
);

const VIA_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  upi_offline: 'UPI (offline)',
  cheque: 'Cheque',
  other: 'Other',
  razorpay: 'Razorpay',
};

function PaymentDetailsCard({
  payment,
  confirmedBy,
}: {
  payment: FeePayment;
  confirmedBy: UserRef | null;
}): JSX.Element {
  return (
    <Card title="Payment details">
      <dl className="space-y-2">
        <Field
          label="Gross amount"
          value={formatINR(payment.amountPaise)}
        />
        {payment.discountPaise > 0 ? (
          <Field label="Discount" value={formatINR(payment.discountPaise)} />
        ) : null}
        <Field label="Net amount" value={formatINR(payment.netAmountPaise)} />
        <Field
          label="Due date"
          value={formatDate(`${payment.dueDate}T00:00:00Z`)}
        />
        <Field
          label="Paid at"
          value={
            payment.paidAt
              ? formatDate(payment.paidAt, { withTime: true })
              : null
          }
        />
        <Field
          label="Paid via"
          value={payment.paidVia ? VIA_LABELS[payment.paidVia] ?? payment.paidVia : null}
        />
        <Field
          label="Reference"
          value={
            payment.paidReference ? (
              <span className="font-mono text-xs">{payment.paidReference}</span>
            ) : null
          }
        />
        <Field
          label="Confirmed by"
          value={confirmedBy?.displayName ?? confirmedBy?.email ?? null}
        />
      </dl>
    </Card>
  );
}

interface StudentHouseholdCardProps {
  student: FeePaymentStudentRef;
  household: HouseholdRef | null;
}

function StudentHouseholdCard({
  student,
  household,
}: StudentHouseholdCardProps): JSX.Element {
  return (
    <Card title="Student & family">
      <div>
        <p className="text-sm font-medium text-[color:var(--color-text)]">
          <Link
            to="/people/students/$studentId"
            params={{ studentId: student.id }}
            className="hover:underline"
          >
            {student.name}
          </Link>
        </p>
        {student.admissionNumber ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            {student.admissionNumber}
          </p>
        ) : null}
      </div>
      <div className="border-t border-[color:var(--color-border)] pt-2">
        <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Family
        </p>
        {household ? (
          <>
            <p className="text-sm">
              <Link
                to="/people/households/$householdId"
                params={{ householdId: household.id }}
                className="font-medium text-[color:var(--color-text)] hover:underline"
              >
                {household.primaryContactName}
              </Link>
            </p>
            {household.contacts && household.contacts.length > 0 ? (
              <ul className="mt-1 space-y-1 text-xs text-[color:var(--color-text-muted)]">
                {household.contacts.map((c) => (
                  <li key={c.id} className="flex flex-wrap gap-x-2">
                    <span className="font-medium text-[color:var(--color-text)]">
                      {c.name}
                    </span>
                    {c.relation ? <span>({c.relation})</span> : null}
                    {c.phone ? <span>{c.phone}</span> : null}
                    {c.email ? <span>{c.email}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-[color:var(--color-text-muted)]">
            No family linked.
          </p>
        )}
      </div>
    </Card>
  );
}

function FeeCycleCard({ cycle }: { cycle: FeeCycle }): JSX.Element {
  return (
    <Card title="Fee cycle">
      <dl className="space-y-2">
        <Field label="Cycle" value={cycleLabel(cycle.year, cycle.month)} />
        <Field
          label="Due date"
          value={formatDate(`${cycle.dueDate}T00:00:00Z`)}
        />
        {cycle.notes ? <Field label="Notes" value={cycle.notes} /> : null}
      </dl>
    </Card>
  );
}

function ClaimHistoryCard({ claim }: { claim: ClaimSummary | null }): JSX.Element {
  if (!claim) {
    return (
      <Card title="Claim history">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          No parent-submitted claim for this payment.
        </p>
      </Card>
    );
  }
  return (
    <Card title="Claim history">
      <p className="text-sm text-[color:var(--color-text)]">
        <Link
          to="/finance/fees/claims/$claimId"
          params={{ claimId: claim.id }}
          className="font-medium hover:underline"
        >
          View claim
        </Link>{' '}
        — submitted {formatDate(claim.createdAt, { withTime: true })}
        {claim.claimedByName ? (
          <>
            {' '}by{' '}
            <span className="text-[color:var(--color-text)]">
              {claim.claimedByName}
            </span>
          </>
        ) : null}
      </p>
      <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Status: {claim.status}
      </p>
    </Card>
  );
}

function ReceiptPdfCard({ payment }: { payment: FeePayment }): JSX.Element {
  // Backend currently does not expose a `pdfGeneratedAt` field, so the best
  // proxy we can show today is the payment's updatedAt — when a receipt is
  // (re)generated the row's updatedAt is touched.
  return (
    <Card title="Receipt PDF">
      <dl className="space-y-2">
        <Field
          label="Receipt #"
          value={
            payment.receiptNumber ? (
              <span className="font-mono text-xs">{payment.receiptNumber}</span>
            ) : (
              <span className="text-[color:var(--color-text-muted)]">
                pending
              </span>
            )
          }
        />
        <Field
          label="Last touched"
          value={formatDate(payment.updatedAt, { withTime: true })}
        />
      </dl>
      <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
        Receipts are immutable artefacts. Use Regenerate PDF only if the
        generated file is corrupt or missing.
      </p>
    </Card>
  );
}
